import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../api/auth';
import { setupAccount } from '../../api/system';


const CATEGORY_OPTIONS = [
    { label: '음식점', value: 'RESTAURANT' },
    { label: '카페',  value: 'CAFE' },
    { label: '미용',  value: 'HAIR' },
    { label: '의류',  value: 'FASHION' },
    { label: '의료',  value: 'MEDICAL' },
    { label: '교육',  value: 'EDUCATION' },
    { label: '스포츠', value: 'SPORTS' },
    { label: '숙박',  value: 'ACCOMMODATION' },
    { label: '기타',  value: 'OTHER' },
];

const STEPS = [
    { n: 1, label: '스토어 생성' },
    { n: 2, label: '번호 매칭' },
    { n: 3, label: '채널 생성' },
];


const OnboardingPage = () => {
    const navigate = useNavigate();
    const { setIsLoggedIn, refreshAuth } = useAuth();
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleLogout = async () => {
        try {
            await logout();
            setIsLoggedIn(false);
            navigate('/auth/login', { replace: true });
        } catch {
            alert('로그아웃 중 오류가 발생했습니다.');
        }
    };

    const [스토어폼, set스토어폼] = useState({ name: '', category: '', bio: '' });
    const [채널폼, set채널폼] = useState({ name: '', bio: '' });

    const handleStoreChange = (e) => {
        const { name, value } = e.target;
        set스토어폼((prev) => ({ ...prev, [name]: value }));
    };

    const handleChannelChange = (e) => {
        const { name, value } = e.target;
        set채널폼((prev) => ({ ...prev, [name]: value }));
    };

    const handleSetup = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await setupAccount({
                store_name: 스토어폼.name,
                store_category: 스토어폼.category,
                channel_name: 채널폼.name,
            });
            await refreshAuth();
            setStep(4);
        } catch {
            alert('설정 중 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <PageWrapper>
            <TopBar>
                <TopLogo>ddiring</TopLogo>
                <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
            </TopBar>
            <Card>
                <StepIndicator>
                    {STEPS.map(({ n, label }, i) => (
                        <StepItem key={n}>
                            <StepCircle $active={step >= n} $current={step === n}>
                                {step > n ? '✓' : n}
                            </StepCircle>
                            <StepLabel $active={step >= n}>{label}</StepLabel>
                            {i < STEPS.length - 1 && <StepLine $done={step > n} />}
                        </StepItem>
                    ))}
                </StepIndicator>

                {step === 1 && (
                    <StepContent>
                        <StepTitle>스토어를 만들어볼게요</StepTitle>
                        <StepDesc>고객이 예약할 수 있는 공간입니다.</StepDesc>
                        <Form onSubmit={(e) => { e.preventDefault(); setStep(2); }}>
                            <Field>
                                <Label>스토어 이름 *</Label>
                                <Input
                                    name="name"
                                    placeholder="예: 홍길동 카페"
                                    value={스토어폼.name}
                                    onChange={handleStoreChange}
                                    required
                                />
                            </Field>
                            <Field>
                                <Label>카테고리 *</Label>
                                <Select
                                    name="category"
                                    value={스토어폼.category}
                                    onChange={handleStoreChange}
                                    required
                                >
                                    <option value="">선택하세요</option>
                                    {CATEGORY_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Select>
                            </Field>
                            <Field>
                                <Label>소개</Label>
                                <Input
                                    name="bio"
                                    placeholder="간단한 소개 (선택)"
                                    value={스토어폼.bio}
                                    onChange={handleStoreChange}
                                />
                            </Field>
                            <NextButton type="submit">다음</NextButton>
                        </Form>
                    </StepContent>
                )}

                {step === 2 && (
                    <StepContent>
                        <StepTitle>전용 번호가 배정됐어요</StepTitle>
                        <StepDesc>고객이 이 번호로 연락하면 AI가 응대합니다.</StepDesc>

                        <NumberBox>
                            <NumberLabel>전용 번호</NumberLabel>
                            <NumberDisplay>050-7123-4567</NumberDisplay>
                            <NumberHint>고객에게 이 번호를 알려주세요</NumberHint>
                        </NumberBox>

                        <NextButton onClick={() => setStep(3)}>다음</NextButton>
                    </StepContent>
                )}

                {step === 3 && (
                    <StepContent>
                        <StepTitle>첫 번째 채널을 만들어요</StepTitle>
                        <StepDesc>채널별로 예약 유형을 구분할 수 있어요.</StepDesc>
                        <Form onSubmit={handleSetup}>
                            <Field>
                                <Label>채널 이름 *</Label>
                                <Input
                                    name="name"
                                    placeholder="예: 헤어 예약"
                                    value={채널폼.name}
                                    onChange={handleChannelChange}
                                    required
                                />
                            </Field>
                            <Field>
                                <Label>소개</Label>
                                <Input
                                    name="bio"
                                    placeholder="간단한 소개 (선택)"
                                    value={채널폼.bio}
                                    onChange={handleChannelChange}
                                />
                            </Field>
                            <NextButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ? '처리 중...' : '완료'}
                            </NextButton>
                        </Form>
                    </StepContent>
                )}

                {step === 4 && (
                    <StepContent $center>
                        <CheckCircle>✓</CheckCircle>
                        <StepTitle>준비 완료!</StepTitle>
                        <StepDesc>스토어와 채널이 성공적으로 만들어졌어요.</StepDesc>
                        <NextButton onClick={() => navigate('/stores', { replace: true })}>채널 보러 가기</NextButton>
                    </StepContent>
                )}
            </Card>
        </PageWrapper>
    );
};


const PageWrapper = styled.div`
    width: 100dvw;
    min-height: 100dvh;
    background: #f7f8fa;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    padding-top: 80px;
    box-sizing: border-box;
`;

const TopBar = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 64px;
    padding: 0 32px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #eeeeee;
    background: #ffffff;
    z-index: 100;
`;

const TopLogo = styled.div`
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 800;
    color: #487BFD;
    letter-spacing: -0.02em;
`;

const LogoutButton = styled.button`
    background: none;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 6px 14px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;

    &:hover {
        border-color: #bbbbbb;
        color: #555555;
    }
`;

const Card = styled.div`
    width: 100%;
    max-width: 480px;
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 20px;
    padding: 40px 40px 48px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const StepIndicator = styled.div`
    display: flex;
    align-items: center;
`;

const StepItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const StepCircle = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    transition: background 0.2s, border-color 0.2s;
    background: ${({ $active }) => ($active ? '#487BFD' : 'transparent')};
    border: 2px solid ${({ $active }) => ($active ? '#487BFD' : '#e0e0e0')};
    color: ${({ $active }) => ($active ? '#ffffff' : '#bbbbbb')};
    box-shadow: ${({ $current }) => ($current ? '0 0 0 4px #487BFD22' : 'none')};
`;

const StepLabel = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 500;
    color: ${({ $active }) => ($active ? '#111111' : '#bbbbbb')};
    white-space: nowrap;
`;

const StepLine = styled.div`
    flex: 1;
    width: 32px;
    height: 2px;
    background: ${({ $done }) => ($done ? '#487BFD' : '#e0e0e0')};
    margin: 0 8px;
    transition: background 0.2s;
`;

const StepContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: ${({ $center }) => ($center ? 'center' : 'stretch')};
    text-align: ${({ $center }) => ($center ? 'center' : 'left')};
`;

const StepTitle = styled.h2`
    font-family: Pretendard;
    font-size: 20px;
    font-weight: 700;
    color: #111111;
    margin: 0;
`;

const StepDesc = styled.p`
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #777777;
    margin: -12px 0 0;
    line-height: 1.6;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Label = styled.label`
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    color: #777777;
`;

const inputBase = `
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 10px 12px;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    color: #111111;
    outline: none;
    width: 100%;
    box-sizing: border-box;

    &::placeholder { color: #c0c0c0; }
    &:focus { border-color: #487BFD; }
`;

const Input = styled.input`${inputBase}`;

const Select = styled.select`
    ${inputBase}
    appearance: none;
    cursor: pointer;
`;

const NumberBox = styled.div`
    background: #f0f2ff;
    border: 1px solid #d0d8ff;
    border-radius: 16px;
    padding: 36px 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
`;

const NumberLabel = styled.span`
    font-family: Pretendard;
    font-size: 11px;
    font-weight: 600;
    color: #aaaaaa;
    text-transform: uppercase;
    letter-spacing: 0.1em;
`;

const numberReveal = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const NumberDisplay = styled.div`
    font-family: Pretendard;
    font-size: 36px;
    font-weight: 800;
    color: #487BFD;
    letter-spacing: 0.04em;
    animation: ${numberReveal} 0.4s ease;
`;

const NumberHint = styled.span`
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    color: #aaaaaa;
    margin-top: 4px;
`;

const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50%       { transform: scale(1.1); }
`;

const CheckCircle = styled.div`
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #487BFD;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: #ffffff;
    animation: ${pulse} 0.5s ease;
`;

const NextButton = styled.button`
    background: #487BFD;
    border: none;
    border-radius: 10px;
    padding: 13px 0;
    width: 100%;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;
    margin-top: 4px;

    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;


export default OnboardingPage;
